'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/src/i18n/navigation';
import { BasicProductDataResponse, DiscountTypeEnums } from '@/src/types/catalog';
import Image from '@/src/components/Image';
import { cn, PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { useParams } from 'next/navigation';
import { Routes } from '@/src/routes/routes';
import SaudiRealls, { TCountryId, TCurrency } from '@/src/components/sarComponent';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import Typography from '@/src/components/ui/typography';
import IsFavorite from '@/src/app/[locale]/stores/components/IsFavorite';
import { useCountryStore } from '@/src/lib/state-stores/currencyStore';

interface ProductCardHandlers {
  scroll?: boolean;
  isFavorite?: boolean;
  isSearchPage?: boolean;
  branchId?: string | number;
  branchCurrency?: TCurrency;
  branchCountryId?: TCountryId;
  storeTime?: string;
}

export function PromotedProduct({
  productDetails,
  scroll = false,
  branchId,
  branchCountryId,
  branchCurrency,
  storeTime,
}: {
  productDetails: BasicProductDataResponse;
} & ProductCardHandlers) {
  const {
    defaultImage,
    isHaveValidDiscount,
    discount,
    price,
    englishName,
    arabicName,
    originalPrice,
    isSoldOut,
    storeEnglishName,
    storeArabicName,
    currencyCode,
    integrationId,
    countryId,
  } = productDetails as BasicProductDataResponse;

  const { storeId, store } = useParams();
  const decodedStore = decodeURIComponent(store as string);
  const currentLang = useLocale() as 'ar' | 'en';
  const t = useTranslations('product');
  const { countryId: countryIdFromStore } = useCountryStore();

  // Logic to determine product and store names based on locale
  const productName = currentLang === 'en' ? englishName : arabicName;
  const storeName = currentLang === 'ar' ? storeArabicName : storeEnglishName;

  const isDigital =
    Boolean(productDetails?.isDigitalCard) ||
    Boolean((productDetails as any)?.isDigital) ||
    (productDetails as any)?.storeType === 3 ||
    (productDetails as any)?.cardType === 3 ||
    (productDetails as any)?.productType === 3 ||
    (productDetails as any)?.productTypeId === 3 ||
    (productDetails as any)?.itemTypeId === 3;

  return (
    <figure
      className={cn(
        // Modern UI Base Styles (Dark, Glow, Rounded)
        'relative z-10 flex h-full w-full justify-between overflow-hidden rounded-2xl bg-white p-6 shadow',
        'hover:shadow-lg hover:shadow-gray-400/50', // Hover glow effect
        // Optional SoldOut Styling
      )}
    >
      <div>
        {/* 1. Upper Content Block (Product Visualization & Fav Button) */}

        {/* 2. Middle Content Block (Typography & Names) */}
        <figcaption className="flex flex-col gap-3 text-start">
          {/* Sale Badge (Top Left, translated design) */}
          {isHaveValidDiscount && (
            <div className="self-start rounded-full bg-red-600/10 px-4 py-1 text-xs font-medium text-red-400">
              {t('sale')}
            </div>
          )}

          {/* Store Name - Body 2 */}
          <Typography variant={'body2'} color={'gray'} component={'h3'} className="text-gray-400">
            {storeName}
          </Typography>

          {/* Product Name - Subtitle 2, Line Clamp */}
          <Typography
            color={'text'}
            component={'h2'}
            lineClamp={2}
            variant={'subtitle2'}
            className="mb-3 text-xl leading-tight font-bold lg:text-2xl"
          >
            {productName}
          </Typography>
        </figcaption>

        {/* 3. Lower Content Block (Price & Actions) */}
        <div className="mt-auto flex flex-col gap-5 border-t border-gray-800 pt-5 font-semibold">
          {/* --- Price Display --- */}
          <div className="flex flex-col text-start">
            {/* Original Price (Strike-through) */}
            <div className="text-gray-500">
              {isHaveValidDiscount && (
                <SaudiRealls
                  price={Number(originalPrice?.toFixed(2))}
                  size="extraSmall"
                  isHaveDiscount={true}
                  currency={currencyCode || (branchCurrency as TCurrency)}
                  countryId={countryId || (branchCountryId as TCountryId) || countryIdFromStore}
                />
              )}
            </div>

            {/* Current Price and Discount Value */}
            <div className="flex items-center gap-3">
              <span className={cn(isHaveValidDiscount && 'text-red-400')}>
                <SaudiRealls
                  price={Number(price?.toFixed(2))}
                  size="small"
                  currency={currencyCode || (branchCurrency as TCurrency)}
                  countryId={countryId || (branchCountryId as TCountryId) || countryIdFromStore}
                  className="text-2xl"
                />
              </span>

              {isHaveValidDiscount && (
                <div className="flex items-center gap-1 rounded-full bg-red-600/10 px-3 py-1 text-xs font-bold text-red-400">
                  (
                  {discount?.discountType === DiscountTypeEnums.Percentage ? (
                    <>{discount?.discountValue}% </>
                  ) : (
                    <SaudiRealls
                      price={Number(discount?.discountValue)}
                      size="extraSmall"
                      currency={currencyCode || branchCurrency}
                      countryId={countryId || branchCountryId || countryIdFromStore}
                    />
                  )}
                  {t('Off')} )
                </div>
              )}
            </div>
          </div>
        </div>

        {/* The main link wrap, overlaying for event tracking */}
        <Link
          href={Routes.productDetails(
            String(branchId || productDetails.branchId),
            String(storeEnglishName || decodedStore),
            productDetails.integrationId,
            productDetails.englishName || '',
          )}
          onClick={() => {
            trackEvent(AmplitudeEvents.product_clicked, {
              shop_id: Number(storeId),
              shop_name: productDetails.storeName,
              branch_id: Number(branchId || productDetails.branchId),
              branch_name: productDetails.branchName,
              product_id: productDetails.integrationId,
              product_name: productName,
              has_discount: productDetails?.isHaveValidDiscount || false,
            });
          }}
          className="absolute inset-0 z-5" // Overlays everything except Fav and Modal
        />
      </div>

      <div className="relative aspect-4/5 w-full max-w-60 overflow-hidden rounded-xl">
        {/* Cyan Glow Effect (Translating the Design concept) */}

        {/* --- Soldout Banner --- */}
        {isSoldOut && (
          <div className="absolute top-5 left-2 z-30 flex items-center justify-center">
            <span className="max-w-max shrink-0 rounded-full bg-gray-500 px-6 py-1 text-sm font-semibold text-white">
              {t('SoldOut')}
            </span>
          </div>
        )}

        {/* --- Back Glow & Image --- */}

        {/* --- Favorite Button (Top Corner, Integrated to Design) --- */}
        {!isDigital && (
          <div
            className="absolute top-0 right-0 z-10 grid size-10 place-items-center rounded-full bg-white/10 shadow-lg backdrop-blur-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IsFavorite
              branch_id={
                productDetails?.branchId ?? storeId?.toString() ?? branchId?.toString() ?? ''
              }
              item_id={productDetails.integrationId}
              size={20} // Slightly bigger
              type={1}
            />
          </div>
        )}

        <Image
          size={PRODUCT_IMAGE_SIZE.MEDIUM}
          alt={`${productDetails.englishName}`}
          src={defaultImage || ''}
          key={defaultImage || ''}
          fill
          className="object-cover" // Interactive zoom
          sizes="(min-width: 1420px) 251px, (min-width: 1040px) calc(16.39vw + 22px), (min-width: 780px) calc(23.75vw - 17px), (min-width: 640px) 28.33vw, calc(44.38vw - 7px)"
        />
      </div>
    </figure>
  );
}
